import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
	addEdge,
	useNodesState,
	useEdgesState,
	Connection,
	Node,
	ReactFlowProvider,
	useReactFlow,
	MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Select, message, Modal } from 'antd';
import InlineLoading from '../../../../components/shared/InlineLoading';
import { PlusOutlined } from '@ant-design/icons';
import { DeletableEdge, BuilderLayout, BuilderToolbar, FlowCanvas } from '../../../../components/builder';
import { nodeTypes } from './components/TreatmentPlanNodes';
import NodePalette from './components/NodePalette';
import NodePropertiesPanel, { BuilderData } from './components/NodePropertiesPanel';
import { createTemplate, fetchTemplate, updateTemplate, fetchTemplates as listTreatmentPlanTemplates, fetchNoteTemplates } from '../../../../api/healthcare/TreatmentPlanApi';
import { fetchForms } from '../../../../api/formApi';
import { fetchAppointmentTypes as fetchApptTypes } from '../../../../api/healthcare/Appointment/AppointmentSettingsApi';
import { fetchTemplates as fetchNotifTemplates } from '../../../../api/communications/templateApi';
import { fetchWorkflowTemplates } from '../../../../api/healthcare/Consultation/ConsultApi';
import { fetchTeamMembers } from '../../../../api/admin/teamApi';
import {
	FlowNode,
	FlowEdge,
	FlowNodeData,
	StageType,
	STAGE_CONFIG,
	ConnectionType,
	computeStageNumbers,
	detectBackEdges,
	TreatmentPlanSectionInfo,
	TreatmentPlanTemplate,
} from '../../../../types/healthcare/TreatmentPlanTypes';
import { fetchCatalogMedications } from '../../../../api/healthcare/MedicationCatalogApi';
import { getGlobalResourceLinks } from '../../../../api/file/patientResourceLinksApi';
import { getGlobalFileResources } from '../../../../api/file/globalFileResourcesApi';
import { fetchCustomTestPackages } from '../../../../api/healthcare/Testing/CustomTestPackageApi';
import { DetailDrawer } from '../../../../components/shared';

export const TEMPLATE_CATEGORIES = [
	'Onboarding',
	'Post-Appointment',
	'Chronic Care',
	'Follow-Up',
	'Wellness',
	'Mental Health',
	'Other',
];

let nodeIdCounter = 0;
const genId = (type: StageType | 'section_group') => `${type}_${Date.now()}_${++nodeIdCounter}`;

const edgeOptions = {
	type: 'deletable',
	style: { strokeWidth: 2, stroke: 'var(--mg-border-strong)' },
	markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
	animated: false,
};

const edgeTypes = { deletable: DeletableEdge };

// Derive edge connection_type from source handle
const getConnectionType = (sourceHandle: string | null | undefined): ConnectionType => {
	if (sourceHandle === 'condition_yes') return 'condition_yes';
	if (sourceHandle === 'condition_no') return 'condition_no';
	if (sourceHandle?.startsWith('option_')) return sourceHandle as ConnectionType;
	return 'default';
};

const getEdgeLabel = (connectionType: ConnectionType, sourceNode?: FlowNode | null) => {
	if (connectionType === 'condition_yes') return 'Yes';
	if (connectionType === 'condition_no') return 'No';
	if (typeof connectionType === 'string' && connectionType.startsWith('option_')) {
		const optionKey = connectionType.slice('option_'.length);
		const options: Array<{ option_key: string; label: string }> =
			sourceNode?.data?.configuration?.options ?? [];
		const match = options.find((o) => o.option_key === optionKey);
		return match?.label ?? optionKey;
	}
	return undefined;
};


interface TPBProps {
	isOpen: boolean;
	onClose: () => void;
	template: TreatmentPlanTemplate;
}

const TreatmentPlanBuilderModalInner: React.FC<TPBProps> = ({ template, isOpen, onClose }) => {
	const { screenToFlowPosition, getNode } = useReactFlow();

	const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
	const [selectedNode, setSelectedNode] = useState<Node<FlowNodeData> | null>(null);
	const [templateName, setTemplateName] = useState('');
	const [templateDescription, setTemplateDescription] = useState('');
	const [templateCategory, setTemplateCategory] = useState<string | undefined>();
	const [isActive, setIsActive] = useState(true);
	const [isPeptidePlan, setIsPeptidePlan] = useState(false);
	const [isGlobal, setIsGlobal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [builderData, setBuilderData] = useState<BuilderData>({
		forms: [],
		practitionerForms: [],
		appointmentTypes: [],
		noteTemplates: [],
		notificationTemplates: [],
		treatmentPlanTemplates: [],
		workflowTemplates: [],
		medicationCatalog: [],
		practitioners: [],
		globalResourceLinks: [],
		globalFileResources: [],
		customTestPackages: [],
		dataLoading: false,
	});

	const edgeTopologyKey = useMemo(
		() => edges.map((e) => `${e.id}:${e.source}:${e.target}`).join('|'),
		[edges]
	);
	const stageNodeCount = useMemo(
		() => nodes.filter((n) => n.type !== 'section_group').length,
		[nodes]
	);

	useEffect(() => {
		const stageNodes = nodes.filter((n) => n.type !== 'section_group') as FlowNode[];
		const currentEdges = edges as FlowEdge[];

		const backEdgeIds = detectBackEdges(stageNodes, currentEdges);
		const backEdgeTargets = new Set(
			currentEdges.filter((e) => backEdgeIds.has(e.id)).map((e) => e.target)
		);
		const stageNums = computeStageNumbers(stageNodes, currentEdges, backEdgeIds);

		setNodes((nds) =>
			nds.map((n) => {
				if (n.type === 'section_group') return n;
				return {
					...n,
					data: {
						...n.data,
						stage_number: stageNums.get(n.id) ?? 1,
						is_loop_target: backEdgeTargets.has(n.id),
					},
				};
			})
		);

		setEdges((eds) =>
			eds.map((e) => ({
				...e,
				data: {
					...(e.data ?? {}),
					// An edge loaded without `data` would otherwise gain one here with no
					// connection_type. Default it to the same 'default' the save path at
					// buildEdgePayload() already falls back to, so the two agree.
					connection_type: e.data?.connection_type ?? 'default',
					is_back_edge: backEdgeIds.has(e.id),
				},
			}))
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edgeTopologyKey, stageNodeCount]);

	// Load builder reference data (forms, appointment types, templates, etc.)
	useEffect(() => {
		setBuilderData((prev) => ({ ...prev, dataLoading: true }));
		Promise.all([
			fetchForms(undefined, 'patient').catch(() => ({ data: [] })),
			fetchForms(undefined, 'practitioner').catch(() => ({ data: [] })),
			fetchApptTypes().catch(() => []),
			fetchNoteTemplates().catch(() => []),
			fetchNotifTemplates().catch(() => ({ data: [] })),
			listTreatmentPlanTemplates().catch(() => []),
			fetchCatalogMedications({}).catch(() => []),
			fetchWorkflowTemplates().catch(() => ({ data: [] })),
			fetchTeamMembers(1, 100).catch(() => ({ data: [], total: 0 })),
			getGlobalResourceLinks().catch(() => []),
			getGlobalFileResources().catch(() => []),
			fetchCustomTestPackages({ active_only: true }).catch(() => []),
		])
			.then(([formsRes, practitionerFormsRes, apptTypesRes, noteTemplatesRes, notifRes, tplRes, catalogRes, workflowRes, teamRes, globalLinksRes, globalFilesRes, customPkgsRes]) => {
				setBuilderData({
					forms: (Array.isArray((formsRes as any).data) ? (formsRes as any).data : []).map((f: any) => ({ id: f.id, title: f.title })),
					practitionerForms: (Array.isArray((practitionerFormsRes as any).data) ? (practitionerFormsRes as any).data : []).map((f: any) => ({ id: f.id, title: f.title })),
					appointmentTypes: (Array.isArray(apptTypesRes) ? apptTypesRes : []).map((a: any) => ({ id: a.id, name: a.name, color: a.color })),
					noteTemplates: (Array.isArray(noteTemplatesRes) ? noteTemplatesRes : []).map((n: any) => ({ id: n.id, name: n.name })),
					notificationTemplates: (Array.isArray((notifRes as any).data) ? (notifRes as any).data : []).map((t: any) => ({ id: t.id, name: t.name, category: t.category })),
					treatmentPlanTemplates: (Array.isArray(tplRes) ? tplRes : []).map((t: any) => ({ id: t.id, name: t.name })),
					medicationCatalog: Array.isArray(catalogRes) ? catalogRes : [],
					workflowTemplates: (Array.isArray((workflowRes as any).templates) ? (workflowRes as any).templates : Array.isArray(workflowRes) ? workflowRes : []).map((t: any) => ({ id: t.id, name: t.name })),
					practitioners: (Array.isArray((teamRes as any).data) ? (teamRes as any).data : []).map((m: any) => ({ id: m.team_member_info?.id ?? m.id, name: `${m.first_name} ${m.last_name}`.trim() })),
					globalResourceLinks: (Array.isArray(globalLinksRes) ? globalLinksRes : []).map((l: any) => ({
						id: l.id,
						name: l.description || l.resource_type || `Link ${l.id}`,
						description: l.description,
					})),
					globalFileResources: (Array.isArray(globalFilesRes) ? globalFilesRes : []).map((f: any) => ({
						id: f.id,
						name: f.name,
						file_type: f.file_type,
					})),
					customTestPackages: (Array.isArray(customPkgsRes) ? customPkgsRes : []).map((p: any) => ({
						id: p.id,
						name: p.name,
					})),
					dataLoading: false,
				});
			})
			.catch(() => {
				setBuilderData((prev) => ({ ...prev, dataLoading: false }));
			});
	}, []);

	// Load template when editing
	useEffect(() => {
		if (template && template.id !== 0) {
			setLoading(true);
			fetchTemplate(Number(template.id))
				.then((tpl) => {
					setTemplateName(tpl.name);
					setTemplateDescription(tpl.description ?? '');
					setTemplateCategory(tpl.category);
					setIsActive(tpl.is_active);
					setIsPeptidePlan(tpl.is_peptide_plan ?? false);
					setIsGlobal(tpl.is_global ?? false);
					if (tpl.canvas_data) {
						const loadedNodes = tpl.canvas_data.nodes as FlowNode[];
						const loadedEdges = tpl.canvas_data.edges as FlowEdge[];

						const hasGroupNodes = loadedNodes.some((n) => n.type === 'section_group');
						const stageNodes = loadedNodes.filter((n) => n.type !== 'section_group');

						if (hasGroupNodes) {
							const groupNodes = loadedNodes
								.filter((n) => n.type === 'section_group')
								.map((n) => ({ ...n, zIndex: -1 }));
							setNodes([...groupNodes, ...stageNodes] as any);
						} else {
							const sections: TreatmentPlanSectionInfo[] = (tpl as any).sections ?? [];
							const groupNodesFromSections = sections.map((s) => {
								const existing = loadedNodes.find((n) => n.type === 'section_group' && (n.data as any).section_key === s.section_key);
								return {
									id: `section_${s.section_key}`,
									type: 'section_group',
									position: existing?.position ?? { x: 0, y: 0 },
									zIndex: -1,
									style: (existing as any)?.style ?? { width: 400, height: 300 },
									data: {
										label: s.name,
										color: s.color,
										section_key: s.section_key,
									},
								};
							});

							const stageNodesWithParent = stageNodes.map((n) => {
								const sectionKey = (n.data as any).section_key;
								if (!sectionKey) return n;
								const parentId = `section_${sectionKey}`;
								return { ...n, parentId };
							});

							setNodes([...groupNodesFromSections, ...stageNodesWithParent] as any);
						}

						setEdges(loadedEdges.map((e) => ({ ...e, type: 'deletable' })));
					}
				})
				.catch(() => message.error('Failed to load template'))
				.finally(() => setLoading(false));
		}
	}, [template.id]);

	// Drop handler —drop from palette onto canvas
	const onDrop = useCallback(
		(event: React.DragEvent) => {
			event.preventDefault();
			const rawType = event.dataTransfer.getData('application/treatment-plan-node') as StageType | 'section_group';
			if (!rawType) return;

			const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });

			if (rawType === 'section_group') {
				const sectionKey = `section_${Date.now()}`;
				const newNode = {
					id: genId('section_group'),
					type: 'section_group',
					position,
					zIndex: -1,
					style: { width: 400, height: 300 },
					data: {
						label: 'New Section',
						color: '#4A90D9',
						section_key: sectionKey,
					},
				};
				setNodes((nds) => nds.concat(newNode as any));
				return;
			}

			const stageType = rawType as StageType;
			const newNode: FlowNode = {
				id: genId(stageType),
				type: stageType,
				position,
				data: {
					stage_key: genId(stageType),
					label: STAGE_CONFIG[stageType]?.label ?? stageType,
					stage_type: stageType,
					configuration: {},
					is_entry_point: false,
				},
			};
			setNodes((nds) => nds.concat(newNode as any));
		},
		[screenToFlowPosition]
	);

	const onDragOver = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	}, []);

	const onPaletteDragStart = (event: React.DragEvent, nodeType: StageType | 'section_group') => {
		event.dataTransfer.setData('application/treatment-plan-node', nodeType);
		event.dataTransfer.effectAllowed = 'move';
	};

	// Connect nodes
	const onConnect = useCallback(
		(connection: Connection) => {
			const connectionType = getConnectionType(connection.sourceHandle);
			const sourceNode = connection.source ? (getNode(connection.source) as FlowNode | undefined) : undefined;
			const edgeLabel = getEdgeLabel(connectionType, sourceNode);

			const edge: FlowEdge = {
				id: `edge_${Date.now()}`,
				source: connection.source!,
				target: connection.target!,
				sourceHandle: connection.sourceHandle ?? undefined,
				targetHandle: connection.targetHandle ?? undefined,
				label: edgeLabel,
				data: { connection_type: connectionType },
				...edgeOptions,
			};
			setEdges((eds) => addEdge(edge as any, eds));
		},
		[setEdges, getNode]
	);

	// Select node
	const onNodeClick = useCallback(
		(_: React.MouseEvent, node: Node) => {
			setSelectedNode(node as Node<FlowNodeData>);
		},
		[]
	);

	const onPaneClick = useCallback(() => {
		setSelectedNode(null);
	}, []);

	// Update selected node data
	const handleNodeUpdate = useCallback(
		(nodeId: string, updates: Partial<FlowNodeData>) => {
			setNodes((nds) =>
				nds.map((n) =>
					n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
				)
			);
			setSelectedNode((prev) =>
				prev && prev.id === nodeId
					? { ...prev, data: { ...prev.data, ...updates } }
					: prev
			);
		},
		[]
	);

	// Delete node & connected edges
	const handleNodeDelete = useCallback(
		(nodeId: string) => {
			Modal.confirm({
				title: 'Remove node?',
				content: 'This will also remove all connections to and from this node.',
				okText: 'Remove',
				okType: 'danger',
				onOk: () => {
					setNodes((nds) => nds.filter((n) => n.id !== nodeId));
					setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
					setSelectedNode(null);
				},
			});
		},
		[]
	);

	// Build payload for backend
	const buildSavePayload = useCallback(
		(currentNodes: FlowNode[], currentEdges: FlowEdge[]) => {
			const stageNodes = currentNodes.filter((n) => n.type !== 'section_group');
			const groupNodes = currentNodes.filter((n) => n.type === 'section_group');

			// Compute logical stage numbers (BFS depth) for every node
			const backEdgeIds = detectBackEdges(stageNodes, currentEdges);
			const stageNums = computeStageNumbers(stageNodes, currentEdges, backEdgeIds);

			// Annotate each node's data with its stage_number so the viewer can group them
			const annotatedNodes: FlowNode[] = stageNodes.map(n => ({
				...n,
				data: { ...n.data, stage_number: stageNums.get(n.id) ?? 1 },
			}));

			const findNearestGroup = (nx: number, ny: number) => {
				if (groupNodes.length === 0) return undefined;
				let best: (typeof groupNodes)[0] | undefined;
				let bestDist = Infinity;
				for (const g of groupNodes) {
					const gx = g.position.x;
					const gy = g.position.y;
					const gw = parseFloat(String((g as any).measured?.width ?? (g as any).width ?? (g as any).style?.width ?? 400));
					const gh = parseFloat(String((g as any).measured?.height ?? (g as any).height ?? (g as any).style?.height ?? 300));
					if (nx >= gx && nx <= gx + gw && ny >= gy && ny <= gy + gh) return g;
					const dx = Math.max(gx - nx, 0, nx - (gx + gw));
					const dy = Math.max(gy - ny, 0, ny - (gy + gh));
					const dist = dx * dx + dy * dy;
					if (dist < bestDist) { bestDist = dist; best = g; }
				}
				return best;
			};

			const stages = annotatedNodes.map((n) => {
				let sectionKey: string | undefined = undefined;

				if ((n as any).parentId) {
					const parent = groupNodes.find((g) => g.id === (n as any).parentId);
					sectionKey = (parent?.data?.section_key as string) ?? undefined;
				}

				if (!sectionKey && groupNodes.length > 0) {
					const match = findNearestGroup(n.position.x, n.position.y);
					sectionKey = match ? ((match.data?.section_key as string) ?? undefined) : undefined;
				}

				return {
					stage_key: n.id,
					stage_type: n.data.stage_type,
					label: n.data.label,
					description: n.data.description,
					configuration: n.data.configuration ?? {},
					position_x: Math.round(n.position.x),
					position_y: Math.round(n.position.y),
					is_entry_point: n.data.is_entry_point ?? false,
					is_required: n.data.is_required ?? true,
					progression_type: 'auto',
					stage_order: stageNums.get(n.id) ?? 0,
					section_key: sectionKey,
				};
			});

			const sections: TreatmentPlanSectionInfo[] = groupNodes.map((n, idx) => ({
				section_key: (n.data.section_key as string) || `section_${n.id}`,
				name: (n.data.label as string) || 'Untitled Section',
				color: (n.data.color as string) || '#4A90D9',
				sort_order: idx,
			}));

			const connections = currentEdges.map((e) => ({
				from_stage_key: e.source,
				to_stage_key: e.target,
				connection_type: e.data?.connection_type ?? 'default',
				label: e.label,
			}));

			return {
				name: templateName,
				description: templateDescription,
				category: templateCategory,
				is_active: isActive,
				is_peptide_plan: isPeptidePlan,
				is_global: isGlobal,
				stages,
				sections,
				connections,
				canvas_data: {
					nodes: currentNodes,
					edges: currentEdges,
				},
			};
		},
		[templateName, templateDescription, templateCategory, isActive, isPeptidePlan, isGlobal]
	);

	const handleSave = useCallback(async () => {
		if (!templateName.trim()) {
			message.warning('Please enter a template name');
			return;
		}

		setSaving(true);
		try {
			const payload = buildSavePayload(nodes as FlowNode[], edges as FlowEdge[]);
			if (template.id && template.id !== 0) {
				await updateTemplate(Number(template.id), payload);
				message.success('Template saved');
			} else {
				const created = await createTemplate(payload);
				message.success('Template created');
			}
		} catch (err: any) {
			message.error(err?.response?.data?.message ?? 'Failed to save template');
		} finally {
			setSaving(false);
		}
	}, [nodes, edges, buildSavePayload]);

	const toolbarExtra = (
		<>
			<Select
				value={templateCategory}
				onChange={setTemplateCategory}
				placeholder="Category"
				style={{ width: 160 }}
				allowClear
				options={TEMPLATE_CATEGORIES.map((c) => ({ value: c, label: c }))}
			/>
			<Select
				value={isActive ? 'active' : 'inactive'}
				onChange={(v) => setIsActive(v === 'active')}
				style={{ width: 110 }}
				options={[
					{ value: 'active', label: '● Active' },
					{ value: 'inactive', label: '○ Inactive' },
				]}
			/>
		</>
	);

	const emptyHint = (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				color: 'var(--mg-text-disabled)',
				pointerEvents: 'none',
			}}
		>
			<PlusOutlined style={{ fontSize: 40, marginBottom: 12 }} />
			<div style={{ fontSize: 16, fontWeight: 500 }}>Drag nodes from the left panel</div>
			<div style={{ fontSize: 13, marginTop: 4 }}>Start with a Trigger node</div>
		</div>
	);

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			// title={template.id === 0 ? "New Treatment Plan Template" : template.name || "Edit Treatment Plan Template"}
			// subtitle={template.id !== 0 ? "Treatment Plan Template" : undefined}
			headerActions={
				<BuilderToolbar
					// onBack={onClose}
					name={templateName}
					onNameChange={setTemplateName}
					namePlaceholder="Template name…"
					description={templateDescription}
					onDescriptionChange={setTemplateDescription}
					extraSlot={toolbarExtra}
					onSave={handleSave}
					saving={saving}
				/>
			}
			noPadding
		>
			{loading ? (
				<InlineLoading minHeight={400} message="Loading template…" />
			) : (
				<BuilderLayout
					palette={<NodePalette onDragStart={onPaletteDragStart} />}
					panel={
						<NodePropertiesPanel
							node={selectedNode}
							onUpdate={handleNodeUpdate}
							onDelete={handleNodeDelete}
							builderData={builderData}
						/>
					}
					panelWidth={340}
				>
					<FlowCanvas
						nodes={nodes}
						edges={edges}
						nodeTypes={nodeTypes as any}
						edgeTypes={edgeTypes}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onConnect={onConnect}
						onNodeClick={onNodeClick}
						onPaneClick={onPaneClick}
						onDrop={onDrop}
						onDragOver={onDragOver}
						defaultEdgeOptions={edgeOptions as any}
						miniMapNodeColor={(n) => STAGE_CONFIG[(n as any).data?.stage_type as StageType]?.color ?? '#94a3b8'}
						emptyState={emptyHint}
					/>
				</BuilderLayout>
			)}
		</DetailDrawer>
	);
};

// ReactFlowProvider must wrap the component that calls useReactFlow, not sit inside it.
const TreatmentPlanBuilderModal: React.FC<TPBProps> = (props) => (
	<ReactFlowProvider>
		<TreatmentPlanBuilderModalInner {...props} />
	</ReactFlowProvider>
);

export default TreatmentPlanBuilderModal;
